/**
 * Modal: Copy all visits of a day to another day (日付の訪問をコピー).
 * RA2: CopyModal – copyFromDate から copyToDate に コピー.
 * RA2: datepicker has startDate/endDate (config); copy_to_date is checked by massagerExists (適用期限外 → コピーできません).
 * Copy target date is not restricted by today.
 */

import React, { useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { AppButton, AppModal } from '@/components/atoms';
import { RA2NDatePicker } from '@/components/organisms/RA2NDatePicker';
import { JA_JP } from '@/components/organisms/RA2NDatePicker/constants';
import { parseDate, parseFormat } from '@/components/organisms/RA2NDatePicker/datepickerLogic';
import useAppToast from '@/hooks/useAppToast';

const dayPickerFormat = parseFormat(JA_JP.format);

interface VisitScheduleCopyDayModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Source date (day to copy from). YYYY-MM-DD */
  copyFromDate: string | null;
  /** Submit: copy to this date. Called with YYYY-MM-DD. Parent validates. */
  onSubmit: (copyToDate: string) => void | Promise<void>;
}

export const VisitScheduleCopyDayModal: React.FC<VisitScheduleCopyDayModalProps> = ({
  isOpen,
  onClose,
  copyFromDate,
  onSubmit,
}) => {
  const [copyToDate, setCopyToDate] = useState('');
  const [loading, setLoading] = useState(false);
  const submitLockRef = useRef(false);
  const toast = useAppToast();

  useEffect(() => {
    if (isOpen) {
      setCopyToDate('');
      setLoading(false);
      submitLockRef.current = false;
    }
  }, [isOpen]);

  const handleSubmit = async () => {
    if (submitLockRef.current) return;
    if (!copyToDate) {
      toast.error('コピー先の日付を入力してください。');
      return;
    }
    submitLockRef.current = true;
    setLoading(true);
    try {
      await onSubmit(copyToDate);
    } finally {
      submitLockRef.current = false;
      setLoading(false);
    }
  };

  const fromLabel = copyFromDate ? dayjs(copyFromDate).format('MM月DD日') : '—';

  return (
    <AppModal
      isOpen={isOpen}
      onClose={onClose}
      title="日付の訪問をコピー"
      maxWidth="max-w-md"
      hideCancelButton={false}
      footer={
        <>
          <AppButton onClick={onClose} disabled={loading}>キャンセル</AppButton>
          <AppButton type="primary" onClick={handleSubmit} disabled={loading} loading={loading}>
            コピー
          </AppButton>
        </>
      }
    >
      <div className="flex flex-wrap items-center gap-2">
        <span>{fromLabel}</span>
        <span>から</span>
        <RA2NDatePicker
          value={copyToDate}
          onChange={value => {
            const trimmed = value.trim();
            if (!trimmed) {
              setCopyToDate('');
              return;
            }

            const parsed = parseDate(trimmed, dayPickerFormat);
            const nextDate = dayjs(parsed);
            if (!nextDate.isValid()) {
              setCopyToDate('');
              return;
            }

            setCopyToDate(nextDate.format('YYYY-MM-DD'));
          }}
          className="w-[180px]"
          placeholder="コピー先の日付"
        />
        <span>に</span>
      </div>
    </AppModal>
  );
};
